/**
 * Tracks the last time the user opened a favorited series so the
 * Browse → Favorites grid can flag shows that gained new episodes
 * since the last view (and bubble them to the top).
 *
 * v1.3.72.
 */

const KEY_PREFIX = "vupp_series_seen_at__";

/** Stable key per show — falls back to series_name when series_id
 *  isn't available (M3U-only catalogs that don't expose Xtream IDs). */
export function seriesSeenKey(series) {
  if (!series) return "";
  return String(series.series_id || series.series_name || "");
}

/** Returns the unix-ms timestamp of the last time the user opened
 *  this series. 0 if never. */
export function getSeriesSeenAt(series) {
  const k = seriesSeenKey(series);
  if (!k) return 0;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + k);
    const n = parseInt(raw || "0", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Mark the series as seen NOW so the NEW badge clears. */
export function markSeriesSeen(series) {
  const k = seriesSeenKey(series);
  if (!k) return;
  try {
    localStorage.setItem(KEY_PREFIX + k, String(Date.now()));
  } catch { /* quota — ignore */ }
}

/** True when the show has gained episodes since the user last
 *  opened it. Returns false for shows the user has never opened
 *  (we don't want every favorited show to scream NEW on first
 *  install) — first-ever view marks them seen via
 *  `seedSeriesSeenAt`. */
export function hasNewEpisodes(series) {
  if (!series) return false;
  const lastAdded = series.last_episode_added_ts || 0;
  if (!lastAdded) return false;
  const seenAt = getSeriesSeenAt(series);
  if (!seenAt) return false;
  // last_episode_added_ts is seconds in some Xtream feeds and ms in
  // others — normalise to ms before comparing.
  const lastAddedMs = lastAdded > 10_000_000_000 ? lastAdded : lastAdded * 1000;
  return lastAddedMs > seenAt;
}

/** Seed a "seen-at" timestamp the FIRST time we encounter a
 *  favorited show. Without this, every show would render with the
 *  NEW badge after the user freshly favorites it. */
export function seedSeriesSeenAtIfMissing(series) {
  const k = seriesSeenKey(series);
  if (!k) return;
  try {
    if (!localStorage.getItem(KEY_PREFIX + k)) {
      localStorage.setItem(KEY_PREFIX + k, String(Date.now()));
    }
  } catch { /* noop */ }
}
